import {Tooltip, Button, message} from "antd";
import {saveAndUpdateQuery, saveThenRunThenUpdateQuery} from "./cohortBuilderSlice";
import React, {useState} from "react";
import {useAppDispatch} from "../../hooks";
import Icon from "@ant-design/icons";
import {Query} from "../HealthcareUsageReport/types/CohortQuery";
import _ from "lodash";
import {AppDispatch} from "../../configureStore";
import {generatePath, useHistory, useRouteMatch} from "react-router-dom";
import {buildStudyCohortRoute, Routes} from "../../pages/routes";
import buttonStyles from '../../components/common/styled/Buttons.module.scss'
import styles from './Actions.module.scss'
import { Run as RunIcon } from './icons/Run'
import { Running as RunningIcon } from './icons/Running'
import {isFinished, isRunning} from "./cohortStatus";

const isEmptyStudy = (study: Query) => {
    return _.filter(
        study.groups,
        function (g) {
            return _.filter(
                g.criteria,
                function (c) { return c.description !== undefined && c.description !== "none"}
            ).length > 0
        }
    ).length === 0
}

const isStudyValid = (study: Query) => {
    const messages = []
    if (isEmptyStudy(study)) {
        messages.push('Cohort is empty.')
    }

    study.groups.forEach(g => {
        if (g.criteria.length === 0 || g.criteria.every(criteria => criteria.description === 'none' || criteria.description === undefined)) {
            messages.push(`Event '${g.title}' has no criteria, either add a criterion or remove the event.`)
        } else {
            const hasEmpty = _.filter(
                g.criteria,
                function (c) { return c.description === 'none' || c.description === undefined }
            ).length > 0

            if (hasEmpty) {
                messages.push(`Event '${g.title}' has some empty criterion, please remove these.`)
            }
        }
    })

    return messages
}

const validateAndRunQuery = (study: Query, dispatch: AppDispatch) => {
    const messages = isStudyValid(study)
    if (messages.length > 0) {
        messages.forEach(m => {
            message.warn(m)
        })
    } else {
        message.info(`The cohort '${study.title}' will now be calculated.`, 3)
        dispatch(saveThenRunThenUpdateQuery(study.id))
    }
}

export const Save: React.FC<{studyId: number}> = ({studyId}) => {
    const dispatch = useAppDispatch()
    const [isSaving, setIsSaving] = useState(false)
    const {params} = useRouteMatch(Routes.Project)
    const history = useHistory()


    return <Button
        className={`${buttonStyles.primary} ${styles['save-btn']}`}
        loading={isSaving}
        type="primary"
        onClick={() => {
            setIsSaving(true)
            return dispatch(saveAndUpdateQuery(studyId))
                .then((query: Query) => {
                    setIsSaving(false)
                    message.info("Cohort saved")
                    history.push(buildStudyCohortRoute(params['projectId'], query.id))
                })
                .catch(() => {
                    setIsSaving(false)
                    message.error("Failed to save cohort")
                })
        }}
        data-testid="save-button"
    >
        Save
    </Button>
}

export const Run: React.FC<{study: Query}> = ({study}) => {
    const dispatch = useAppDispatch()
    const running = isRunning(study)
    const buttonText = running ? 'Running' : 'Run'
    const ButtonIcon = running ? RunningIcon : RunIcon
    const buttonClass = running ? buttonStyles.alert : buttonStyles.greenPrimary

    if (isFinished(study)) {
        return null
    }

    const needsCriteria = isEmptyStudy(study)

    const RunButton = <Button
                className={`${buttonClass} ${styles['run-btn']}`}
                icon={<Icon className={styles['run-btn-icon']} component={ButtonIcon}/>}
                disabled={study.id < 0 || needsCriteria || running}
                onClick={() => { validateAndRunQuery(study, dispatch) }}
                data-testid="run-button"
            >
                {buttonText}
            </Button>

    return needsCriteria ? <Tooltip title="No criteria has been defined for this cohort">
        {RunButton}
    </Tooltip> : RunButton
}

export const Next: React.FC<{study: Query}> = ({study}) => {
    const { path, params } = useRouteMatch(Routes.Project)
    const history = useHistory()
    const dispatch = useAppDispatch()
    const [isSaving, setIsSaving] = useState(false)


    return <Button
        type="primary"
        className={buttonStyles.primary}
        loading={isSaving}
        onClick={async () => {
            setIsSaving(true)
            const savedStudy = await dispatch(saveAndUpdateQuery(study.id))
            const nextPath = generatePath(path, {
                ...params,
                study: 'study',
                studyId: savedStudy.id,
                subpage: undefined
            })
            history.push(nextPath, {fromStudyPeriod: true})
        }}
        data-testid="next-button"
    >
        Next
    </Button>

}